'use client';

import { useTranslations } from 'next-intl';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// SAK subtype string (as stored in lawyer_entities.type) → i18n key under
// Lawyers.sakSubtype. The canonical SAK source strings are Slovak; the
// English locale localizes via the same key. Module-private — surfaces
// that need the localized label go through ``localizeSakSubtype`` so
// the lookup stays one-place.
const SAK_SUBTYPE_KEYS: Readonly<Record<string, string>> = {
  Advokát: 'advokat',
  'Usadený euroadvokát': 'usadenyEuroadvokat',
  'Medzinárodný advokát': 'medzinarodnyAdvokat',
};

/**
 * Resolve a SAK subtype string to its localized label via the
 * `Lawyers.sakSubtype.*` namespace. Returns the raw input unchanged when
 * the subtype isn't keyed yet (defensive fallback for a future SAK
 * category we haven't seen). Returns null for null/empty input so
 * callers can chain it with `??`.
 */
export function localizeSakSubtype(
  subtype: string | null | undefined,
  t: (key: string) => string
): string | null {
  if (subtype == null || subtype === '') return null;
  const key = SAK_SUBTYPE_KEYS[subtype];
  if (key == null) return subtype;
  try {
    return t(key);
  } catch {
    return subtype;
  }
}

// Subtypes that ARE the default for their profession_type — hidden by
// this component because the directory context (lawyer card) already
// communicates the profession. Cross-jurisdictional subtypes ("Usadený
// euroadvokát", "Medzinárodný advokát") are the load-bearing differentiators
// and DO render. See #4541.
const DEFAULT_SUBTYPES_BY_PROFESSION: Readonly<
  Record<string, ReadonlySet<string>>
> = {
  lawyer: new Set(['Advokát']),
};

/**
 * Returns true when the subtype is null / empty OR equals the
 * default-subtype for the given profession_type. Used by
 * ProfessionalSubtypeBadge to suppress the redundant badge.
 */
export function isDefaultSubtype(
  professionType: string | null | undefined,
  type: string | null | undefined
): boolean {
  if (type == null || type === '') return true;
  if (!professionType) return false;
  return DEFAULT_SUBTYPES_BY_PROFESSION[professionType]?.has(type) ?? false;
}

/**
 * Issue #4541 — renders the SAK registry subtype as a small Badge when
 * the lawyer is cross-jurisdictional (Usadený euroadvokát / Medzinárodný
 * advokát). For a plain "Advokát" the badge is suppressed because the
 * surrounding directory UI already communicates the profession.
 */
export function ProfessionalSubtypeBadge({
  professional,
  className,
}: {
  professional: {
    profession_type?: string | null;
    type?: string | null;
  };
  className?: string;
}) {
  const t = useTranslations('Lawyers.sakSubtype');

  if (isDefaultSubtype(professional.profession_type, professional.type)) {
    return null;
  }

  const label = localizeSakSubtype(
    professional.type,
    t as (key: string) => string
  );
  if (!label) return null;

  return (
    <Badge
      variant='outline'
      className={cn(
        'border-brand-tertiary/30 bg-brand-tertiary/10 text-brand-tertiary',
        'text-[10px] py-0 px-1.5 font-medium leading-tight',
        className
      )}
    >
      {label}
    </Badge>
  );
}
